import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { Button } from '@opendate-app/setlist';
import LogDonationModal from '../../ContactsDetail/components/LogDonationModal';
import PledgeFormModal from '../../shared/components/pledges/PledgeFormModal';
import formatPledgeAmount from '../../shared/utils/formatPledgeAmount';
import DealSideRow from './DealSideRow';
import { formatCloseDate } from '../../Deals/components/dealStages';
import client from '../../../shared/api/client';

const toId = (value) => (value === null || value === undefined ? null : Number(value));

const findOption = (options, id) => (id ? (options || []).find((option) => option.id === id) : undefined);

const positiveAmount = (value) => {
  const numeric = Number(value);
  return value !== null && value !== undefined && value !== '' && Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

export function conversionAmount(deal) {
  return positiveAmount(deal.committed_amount) ?? positiveAmount(deal.amount);
}

export function giftInitialValues(deal, pledgeTeam) {
  const amount = conversionAmount(deal);
  const givingCampaign = findOption(pledgeTeam.giving_campaigns, toId(deal.giving_campaign_id)) || null;
  const fund = givingCampaign ? findOption(pledgeTeam.funds, toId(deal.fund_id)) : undefined;

  return {
    amount: amount === null ? '' : amount.toFixed(2),
    givingCampaign,
    fundId: fund ? fund.id : null,
  };
}

export function pledgeInitialValues(deal, pledgeTeam) {
  const amount = conversionAmount(deal);
  const fund = findOption(pledgeTeam.funds, toId(deal.fund_id));
  const campaign = findOption(pledgeTeam.giving_campaigns, toId(deal.giving_campaign_id));
  const solicitor = findOption(pledgeTeam.members, toId(deal.owner_team_membership_id));

  return {
    name: deal.name || '',
    totalAmountCents: amount === null ? 0 : Math.round(amount * 100),
    fundId: fund ? fund.id : null,
    givingCampaignId: campaign ? campaign.id : null,
    solicitorId: solicitor ? solicitor.id : null,
  };
}

function ConvertedSummary({ deal }) {
  const order = deal.converted_order;
  const pledge = deal.converted_pledge;

  if (order) {
    return (
      <>
        <DealSideRow label="Gift" value={<a href={order.url}>{`Gift #${order.id}`}</a>} />
        <DealSideRow label="Amount" value={formatPledgeAmount(Math.round(Number(order.amount) * 100), { withDecimals: true })} />
        <DealSideRow label="Date" value={formatCloseDate(order.date)} />
      </>
    );
  }

  if (pledge) {
    return (
      <>
        <DealSideRow label="Pledge" value={<a href={pledge.url}>{pledge.display_number}</a>} />
        <DealSideRow label="Name" value={pledge.name} />
        <DealSideRow label="Total" value={formatPledgeAmount(pledge.total_amount_cents, { withDecimals: true })} />
      </>
    );
  }

  return <p className="setlist-text-sm text-muted mb-0">The gift or pledge this ask became has been removed.</p>;
}

ConvertedSummary.propTypes = {
  deal: PropTypes.shape({
    converted_order: PropTypes.shape({
      id: PropTypes.number,
      url: PropTypes.string,
      amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      date: PropTypes.string,
    }),
    converted_pledge: PropTypes.shape({
      url: PropTypes.string,
      display_number: PropTypes.string,
      name: PropTypes.string,
      total_amount_cents: PropTypes.number,
    }),
  }).isRequired,
};

function DealConversionCard({ deal, conversion, csrfToken, onConverted }) {
  const [openModal, setOpenModal] = useState(null);
  const teamId = conversion?.pledge_form_team?.id;

  const previewSchedule = useCallback(async (attrs) => {
    const { data } = await client.post(`/teams/${teamId}/crm/pledges/preview_schedule.json`, { pledge: attrs });
    return data;
  }, [teamId]);

  const createPledge = useCallback(async (attrs) => {
    const { data } = await client.post(`/teams/${teamId}/crm/pledges.json`, { pledge: attrs, deal_id: deal.id });
    return data;
  }, [teamId, deal.id]);

  if (deal.converted) {
    return (
      <div className="deal-side-card" data-testid="deal-conversion-card">
        <div className="deal-side-card-title">Converted</div>
        <ConvertedSummary deal={deal} />
      </div>
    );
  }

  if (!conversion) return null;
  if (!deal.convertible) return null;

  const canRecordGift = conversion.can_record_gift;
  const canCreatePledge = conversion.can_create_pledge;
  if (!canRecordGift && !canCreatePledge) return null;

  const donor = deal.conversion_donor;
  const pledgeTeam = conversion.pledge_form_team;
  const isOrganization = donor.type === 'organization';
  const fan = isOrganization ? null : donor;
  const organization = isOrganization ? donor : null;

  const closeModal = () => setOpenModal(null);
  const finishConversion = () => {
    setOpenModal(null);
    onConverted();
  };

  return (
    <div className="deal-side-card" data-testid="deal-conversion-card">
      <div className="deal-side-card-title">Won ask</div>
      <p className="setlist-text-sm text-muted">
        Record the gift or pledge this ask became. It stays linked to the ask.
      </p>
      <div className="d-flex flex-wrap" style={{ gap: 8 }}>
        {canRecordGift && (
          <Button variant="primary" size="sm" onClick={() => setOpenModal('gift')}>Record gift</Button>
        )}
        {canCreatePledge && (
          <Button variant="secondary" size="sm" onClick={() => setOpenModal('pledge')}>Create pledge</Button>
        )}
      </div>

      {openModal === 'gift' && (
        <LogDonationModal
          team={pledgeTeam}
          contact={fan}
          organization={organization}
          csrfToken={csrfToken}
          customFieldDefinitions={conversion.donation_custom_field_definitions}
          initialValues={giftInitialValues(deal, pledgeTeam)}
          dealId={deal.id}
          onClose={closeModal}
          onRecorded={onConverted}
        />
      )}

      {openModal === 'pledge' && (
        <PledgeFormModal
          mode="create"
          team={pledgeTeam}
          fan={fan}
          organization={organization}
          initialValues={pledgeInitialValues(deal, pledgeTeam)}
          previewSchedule={previewSchedule}
          createPledge={createPledge}
          onClose={closeModal}
          onSaved={finishConversion}
        />
      )}
    </div>
  );
}

DealConversionCard.propTypes = {
  deal: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    convertible: PropTypes.bool,
    converted: PropTypes.bool,
    conversion_donor: PropTypes.shape({
      type: PropTypes.oneOf(['fan', 'organization']),
      id: PropTypes.number,
    }),
  }).isRequired,
  conversion: PropTypes.shape({
    can_record_gift: PropTypes.bool,
    can_create_pledge: PropTypes.bool,
    pledge_form_team: PropTypes.shape({
      id: PropTypes.number,
      funds: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
      giving_campaigns: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
      members: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    }),
    donation_custom_field_definitions: PropTypes.arrayOf(PropTypes.object),
  }),
  csrfToken: PropTypes.string,
  onConverted: PropTypes.func.isRequired,
};

export default DealConversionCard;
